import { DOCUMENT } from '@angular/common';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { PortalAuthInfoApi } from './portal-auth-info.api';
import { PortalTokenStore } from './portal-token.store';
import { PortalAuthApi } from './portal-auth.api';
import { PortalUserMetadata, PortalUserInfo } from '../models/auth.model';
import { PortalSubscriptionAgreement } from '../../agreements/models/agreements.model';
import { logoutPortalUser } from './portal-auth-logout';
import { clearAllPortalStorage } from './portal-auth-storage';
import { PortalBrandingRuntimeService } from '../../shell/services/portal-branding-runtime';
import { PORTAL_INACTIVE_REASON, PORTAL_PATIENT_INACTIVE, REASON_QUERY_PARAM } from '../models/portal-error-codes';
import { HttpStatusError } from '@shared/services/api';

function getUserMetadata(user: unknown): PortalUserMetadata | null {
  if (!user || typeof user !== 'object') {
    return null;
  }

  if (!('user_metadata' in user)) {
    return null;
  }

  const meta = (user as PortalUserInfo).user_metadata;

  if (!meta || typeof meta !== 'object') {
    return null;
  }

  return meta;
}

@Injectable({ providedIn: 'root' })
export class PortalSessionStore {
  private readonly authInfoApi = inject(PortalAuthInfoApi);
  private readonly portalTokenStore = inject(PortalTokenStore);
  private readonly authApi = inject(PortalAuthApi);
  private readonly router = inject(Router);
  private readonly doc = inject(DOCUMENT);
  private readonly brandingRuntime = inject(PortalBrandingRuntimeService);

  private readonly _backendUser = signal<PortalUserInfo | null>(null);
  private lastLoadedKey: string | null = null;
  private readonly _loaded = signal<boolean>(false);
  private readonly loadedWaiters: Array<() => void> = [];

  readonly metadata = computed<PortalUserMetadata | null>(() => {
    return getUserMetadata(this._backendUser());
  });

  private readonly _error = signal<string | null>(null);
  private readonly _agreedOverride = signal<boolean>(false);
  private readonly _currentAgreement = signal<PortalSubscriptionAgreement | null>(null);
  private readonly _needsAgreement = signal<boolean>(false);
  private readonly _isFirstAgreement = signal<boolean>(false);
  private readonly _needsCreditCard = signal<boolean>(false);
  private readonly _creditCardOverride = signal<boolean>(false);
  private readonly _needsInsuranceCard = signal<boolean>(false);
  private readonly _insuranceCardOverride = signal<boolean>(false);
  private readonly _needsDriversLicense = signal<boolean>(false);
  private readonly _driversLicenseOverride = signal<boolean>(false);
  private readonly _needsGuardianSignature = signal<boolean>(false);
  private readonly _guardianSignatureOverride = signal<boolean>(false);
  /** Whether this client allows patients to send/reply messages. Defaults to true (messaging enabled). */
  private readonly _messagingSendEnabled = signal<boolean>(true);
  /** Whether this client allows patients to edit their own demographics (RIZ-9586). Defaults to false. */
  private readonly _allowDemographicEdit = signal<boolean>(false);
  /** Per-client portal tool visibility (toolId -> visible). Absent key = visible (fail-safe). */
  private readonly _portalToolVisibility = signal<Record<string, boolean>>({});
  /** Whether this client allows patients to cancel appointments. Defaults to true (cancellation allowed). */
  private readonly _appointmentCancellationEnabled = signal<boolean>(true);
  /** Whether this client renders Form.io date fields in US order (MM/DD/YYYY). Defaults to false. */
  private readonly _useUSFormDateFormat = signal<boolean>(false);
  private readonly _portalInactive = signal<boolean>(false);

  /** True when the backend reported that portal access is inactive for the signed-in patient. */
  readonly portalInactive = computed<boolean>(() => this._portalInactive());

  readonly isGuardian = computed<boolean>(() => {
    const meta = this.metadata();
    return !!meta?.guardianGuid;
  });

  readonly hasCompletedAgreements = computed<boolean>(() => {
    if (this._agreedOverride()) {
      return true;
    }
    return !this._needsAgreement();
  });

  readonly hasCompletedCreditCard = computed<boolean>(() => {
    if (this._creditCardOverride()) {
      return true;
    }
    return !this._needsCreditCard();
  });

  readonly hasCompletedInsuranceCard = computed<boolean>(() => {
    if (this._insuranceCardOverride()) {
      return true;
    }
    return !this._needsInsuranceCard();
  });

  readonly hasCompletedDriversLicense = computed<boolean>(() => {
    if (this._driversLicenseOverride()) {
      return true;
    }
    return !this._needsDriversLicense();
  });

  readonly hasCompletedGuardianSignature = computed<boolean>(() => {
    if (this._guardianSignatureOverride()) {
      return true;
    }
    return !this._needsGuardianSignature();
  });

  /** Whether the client allows patients/guardians to send or reply to messages from the portal. */
  readonly messagingSendEnabled = computed<boolean>(() => this._messagingSendEnabled());
  /** Whether the client allows patients/guardians to edit their own demographics from the portal. */
  readonly allowDemographicEdit = computed<boolean>(() => this._allowDemographicEdit());
  /** Per-client portal tool visibility map (toolId -> visible). Absent key = visible. */
  readonly portalToolVisibility = computed<Record<string, boolean>>(() => this._portalToolVisibility());
  /** Whether the client allows patients/guardians to cancel scheduled appointments from the portal. */
  readonly appointmentCancellationEnabled = computed<boolean>(() => this._appointmentCancellationEnabled());
  /** Whether Form.io date fields render in US order (MM/DD/YYYY) for this client. Backs the shared renderer's gate token. */
  readonly useUSFormDateFormat = computed<boolean>(() => this._useUSFormDateFormat());
  /**
   * Whether a portal tool is visible for this client. Absent key = visible (fail-safe).
   * Single home for the fail-safe polarity — guard, nav, and notification filter all read through this.
   */
  readonly isToolVisible = (toolId: string): boolean => this._portalToolVisibility()[toolId] !== false;
  readonly currentAgreement = computed<PortalSubscriptionAgreement | null>(() => this._currentAgreement());
  readonly needsAgreement = computed<boolean>(() => this._needsAgreement());
  readonly isFirstAgreement = computed<boolean>(() => this._isFirstAgreement());

  readonly patientGuid = computed<string | null>(() => {
    const meta = this.metadata();
    return meta?.patientGuid ?? null;
  });

  readonly guardianGuid = computed<string | null>(() => {
    const meta = this.metadata();
    return meta?.guardianGuid ?? null;
  });

  readonly clientName = computed<string>(() => {
    const meta = this.metadata();
    return meta?.clientName ?? '';
  });

  readonly clientGuid = computed<string | null>(() => {
    const meta = this.metadata();
    return meta?.clientGuid ?? null;
  });

  markAgreed(): void {
    this._agreedOverride.set(true);
    this._needsAgreement.set(false);
  }

  markCreditCardComplete(): void {
    this._creditCardOverride.set(true);
    this._needsCreditCard.set(false);
  }

  markInsuranceCardComplete(): void {
    this._insuranceCardOverride.set(true);
    this._needsInsuranceCard.set(false);
  }

  markDriversLicenseComplete(): void {
    this._driversLicenseOverride.set(true);
    this._needsDriversLicense.set(false);
  }

  markGuardianSignatureComplete(): void {
    this._guardianSignatureOverride.set(true);
    this._needsGuardianSignature.set(false);
  }

  async ensureLoaded(): Promise<void> {
    if (this._loaded()) {
      return;
    }

    await new Promise<void>(resolve => {
      this.loadedWaiters.push(resolve);
    });
  }

  async reloadSession(): Promise<void> {
    this.lastLoadedKey = null;
    this._loaded.set(false);
    await this.loadAuthInfo();
  }

  constructor() {
    effect(() => {
      const hasSession = this.portalTokenStore.hasSession();

      if (!hasSession) {
        this.lastLoadedKey = null;
        this.resetSessionSignals();
        this._loaded.set(true);
        this.flushLoadedWaiters();
        return;
      }

      const subject = this.portalTokenStore.subject();
      const key = `session:${subject}`;
      if (this.lastLoadedKey === key) {
        return;
      }

      this.lastLoadedKey = key;
      this._loaded.set(false);
      void this.loadAuthInfo();
    });
  }

  private static readonly AUTH_INFO_MAX_RETRIES = 5;
  private static readonly AUTH_INFO_RETRY_DELAY_MS = 5000;

  private async loadAuthInfo(): Promise<void> {
    this._error.set(null);
    this._portalInactive.set(false);
    for (let attempt = 1; attempt <= PortalSessionStore.AUTH_INFO_MAX_RETRIES; attempt++) {
      try {
        const authInfo = await this.authInfoApi.getAuthInfo();
        this._backendUser.set(authInfo);

        if (authInfo.branding) {
          this.brandingRuntime.applyFromDto(authInfo.branding);
        } else {
          this.brandingRuntime.applyFromDto({});
        }

        const requirements = authInfo.requirements;
        if (requirements) {
          this._needsAgreement.set(requirements.needsAgreement);
          this._isFirstAgreement.set(requirements.isFirstTime);
          this._currentAgreement.set(requirements.agreement ?? null);
          this._needsCreditCard.set(requirements.requiresCreditCard && !requirements.hasSavedCard);
          this._needsInsuranceCard.set(requirements.requiresInsuranceCard && !requirements.hasInsuranceCard);
          this._needsDriversLicense.set(requirements.requiresDriversLicense && !requirements.hasDriversLicense);
          this._needsGuardianSignature.set(requirements.needsGuardianSignature ?? false);
        }

        this._messagingSendEnabled.set(authInfo.features?.messagingSendEnabled ?? true);
        this._allowDemographicEdit.set(authInfo.features?.allowDemographicEdit ?? false);
        this._portalToolVisibility.set(authInfo.features?.portalToolVisibility ?? {});
        this._appointmentCancellationEnabled.set(authInfo.features?.appointmentCancellationEnabled ?? true);
        this._useUSFormDateFormat.set(authInfo.features?.useUSFormDateFormat ?? false);

        this._loaded.set(true);
        this.flushLoadedWaiters();
        return;
      } catch (err) {
        if (this.isPortalInactiveError(err)) {
          this._portalInactive.set(true);
          clearAllPortalStorage(this.doc);
          this.portalTokenStore.clearTokens();
          this.resetSessionSignals();
          void this.router.navigate(['/signin'], { queryParams: { [REASON_QUERY_PARAM]: PORTAL_INACTIVE_REASON } });
          this._loaded.set(true);
          this.flushLoadedWaiters();
          return;
        }

        if (attempt < PortalSessionStore.AUTH_INFO_MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, PortalSessionStore.AUTH_INFO_RETRY_DELAY_MS));
        }
      }
    }

    await logoutPortalUser(this.router, this.doc, this.portalTokenStore, this.authApi);
    this.resetSessionSignals();
    this.flushLoadedWaiters();
  }

  private isPortalInactiveError(err: unknown): boolean {
    return err instanceof HttpStatusError && err.status === 403 && err.code === PORTAL_PATIENT_INACTIVE;
  }

  /** Clears the loaded session state. Does NOT touch `_portalInactive` — that flag outlives the teardown. */
  private resetSessionSignals(): void {
    this._backendUser.set(null);
    this._agreedOverride.set(false);
    this._currentAgreement.set(null);
    this._needsAgreement.set(false);
    this._isFirstAgreement.set(false);
    this._needsCreditCard.set(false);
    this._creditCardOverride.set(false);
    this._needsInsuranceCard.set(false);
    this._insuranceCardOverride.set(false);
    this._needsDriversLicense.set(false);
    this._driversLicenseOverride.set(false);
    this._needsGuardianSignature.set(false);
    this._guardianSignatureOverride.set(false);
    this._messagingSendEnabled.set(true);
    this._allowDemographicEdit.set(false);
    this._portalToolVisibility.set({});
    this._appointmentCancellationEnabled.set(true);
    this._useUSFormDateFormat.set(false);
  }

  private flushLoadedWaiters(): void {
    if (!this._loaded()) {
      return;
    }
    while (this.loadedWaiters.length > 0) {
      const resolve = this.loadedWaiters.shift();
      if (resolve) {
        resolve();
      }
    }
  }
}
